import { Box, Button, Checkbox, Chip, CircularProgress, Paper, Skeleton, Tooltip, Typography } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { useTranslation } from 'react-i18next';

import { CategoryNode, TYPE_HAS_CHECKBOX, TYPE_LABEL } from '../growCategoryRestriction.types';
import { countAllNodes, isExpandableNode } from '../growCategoryRestriction.helpers';
import { LEVEL_INDENT, LEVEL_TAG_SX, LevelIcon, ROW_BG } from '../growCategoryRestriction.visual';
import {
    getHierarchyRowStyles,
    getHierarchyTypographySx,
    renderHierarchyExpandIcon,
    ChildCountBadge,
} from '../growCategoryRestriction.rowHelpers';
import { useCategoryHierarchyTree } from '../hooks';
import { GCR } from '../growCategoryRestriction.classes';

// Re-export for existing consumers
export { normalizeCategoryTree, isExpandableNode, collectRestrictedIds } from '../growCategoryRestriction.helpers';
export { ICON_COLOR, LEVEL_TAG_SX, LevelIcon } from '../growCategoryRestriction.visual';

// ─── Single tree row ──────────────────────────────────────────────────────────

interface TreeRowProps {
    node: CategoryNode;
    ancestors: CategoryNode[];
    restrictedIds: Set<number>;
    onToggleExpand: (node: CategoryNode) => void;
    onToggleRestrict: (node: CategoryNode, checked: boolean, ancestors: CategoryNode[]) => void;
}

const TreeRow = ({ node, ancestors, restrictedIds, onToggleExpand, onToggleRestrict }: TreeRowProps) => {
    const { t } = useTranslation();
    const hasCheckbox = TYPE_HAS_CHECKBOX[node.typeName];
    const expandable = isExpandableNode(node);
    const indent = LEVEL_INDENT[node.typeName];
    const isRestricted = restrictedIds.has(node.categoryId);
    const childCount = node.isExpanded && node.children?.length ? node.children.length : 0;
    const rowStyles = getHierarchyRowStyles(node, isRestricted, expandable, ROW_BG);

    return (
        <Box
            onClick={() => {
                if (expandable) onToggleExpand(node);
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                pl: `${indent + 8}px`,
                pr: 1.5,
                py: '5px',
                mx: '4px',
                my: '2px',
                minHeight: 34,
                gap: '6px',
                borderRadius: '8px',
                cursor: rowStyles.cursor,
                backgroundColor: rowStyles.backgroundColor,
                borderLeft: rowStyles.borderLeft,
                transition: 'background-color 0.15s, box-shadow 0.15s, border-left-color 0.15s',
                '&:hover': {
                    backgroundColor: rowStyles.hoverBackgroundColor,
                    boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
                    borderLeftColor: rowStyles.hoverBorderLeftColor,
                },
            }}
        >
            <Box
                sx={{
                    width: 20,
                    height: 20,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: node.isExpanded ? 'primary.main' : 'text.secondary',
                    transition: 'color 0.15s',
                }}
            >
                {node.isLoadingChildren ? (
                    <CircularProgress size={12} thickness={5} />
                ) : (
                    renderHierarchyExpandIcon(node.isLoadingChildren, expandable, node.isExpanded)
                )}
            </Box>

            {hasCheckbox ? (
                <Checkbox
                    size="small"
                    checked={isRestricted}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(_, checked) => onToggleRestrict(node, checked, ancestors)}
                    sx={{
                        p: '2px',
                        flexShrink: 0,
                        color: 'text.disabled',
                        '&.Mui-checked': { color: 'error.main' },
                        '& .MuiSvgIcon-root': { fontSize: 16 },
                    }}
                />
            ) : (
                <Box sx={{ width: 24, flexShrink: 0 }} />
            )}

            <LevelIcon typeName={node.typeName} />

            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
                <Tooltip
                    title={!node.isActive ? t('growCategoryRestriction.inactiveCategory') : node.description}
                    enterDelay={!node.isActive ? 200 : 700}
                    disableInteractive
                >
                    <Typography noWrap sx={getHierarchyTypographySx(node, isRestricted)}>
                        {node.description}
                    </Typography>
                </Tooltip>

                <ChildCountBadge count={childCount} />
            </Box>

            <Chip
                label={TYPE_LABEL[node.typeName]}
                size="small"
                sx={{ ...LEVEL_TAG_SX[node.typeName], minWidth: 36, borderRadius: '6px', flexShrink: 0 }}
            />
        </Box>
    );
};

// ─── Recursive tree branch ────────────────────────────────────────────────────

interface TreeBranchProps {
    nodes: CategoryNode[];
    ancestors: CategoryNode[];
    restrictedIds: Set<number>;
    onToggleExpand: (node: CategoryNode) => void;
    onToggleRestrict: (node: CategoryNode, checked: boolean, ancestors: CategoryNode[]) => void;
}

const TreeBranch = ({ nodes, ancestors, restrictedIds, onToggleExpand, onToggleRestrict }: TreeBranchProps) => (
    <>
        {nodes.map((node) => (
            <Box key={node.categoryId}>
                <TreeRow
                    node={node}
                    ancestors={ancestors}
                    restrictedIds={restrictedIds}
                    onToggleExpand={onToggleExpand}
                    onToggleRestrict={onToggleRestrict}
                />
                {node.isExpanded && !!node.children?.length && (
                    <TreeBranch
                        nodes={node.children}
                        ancestors={[...ancestors, node]}
                        restrictedIds={restrictedIds}
                        onToggleExpand={onToggleExpand}
                        onToggleRestrict={onToggleRestrict}
                    />
                )}
                {node.isExpanded && node.isLoadingChildren && !node.children?.length && (
                    <Box sx={{ pl: `${LEVEL_INDENT[node.typeName] + 36}px`, py: 0.5 }}>
                        <CircularProgress size={14} thickness={5} />
                    </Box>
                )}
            </Box>
        ))}
    </>
);

// ─── Main component ───────────────────────────────────────────────────────────

export interface CategoryHierarchyProps {
    countryId: number | '';
    overrideNodes?: CategoryNode[] | null;
    restrictedIds: Set<number>;
    onToggleRestrict: (node: CategoryNode, checked: boolean, ancestors: CategoryNode[]) => void;
    onDiscoverRestrictedIds?: (ids: Set<number>) => void;
    onClearFilter?: () => void;
    reloadKey?: number;
    isRestricting?: boolean;
}

const headerBtnSx = {
    height: 32,
    minWidth: '75%',
    px: 1.5,
    border: '1px solid rgba(0,0,0,0.12)',
    borderRadius: '6px',
    color: 'text.secondary',
    textTransform: 'none' as const,
    fontSize: 12,
    fontWeight: 600,
    '& .MuiButton-startIcon': { mr: 0.75, ml: -0.25 },
    transition: 'all 0.15s',
};

export const CategoryHierarchy = ({
    countryId,
    overrideNodes,
    restrictedIds,
    onToggleRestrict,
    onDiscoverRestrictedIds,
    onClearFilter,
    reloadKey = 0,
    isRestricting = false,
}: CategoryHierarchyProps) => {
    const { t } = useTranslation();
    const { rootNodes, isLoadingRoot, isBusy, collapseAll, toggleExpand } = useCategoryHierarchyTree({
        countryId,
        overrideNodes,
        reloadKey,
        isRestricting,
        onDiscoverRestrictedIds,
    });

    const totalLoaded = countAllNodes(rootNodes);

    return (
        <Paper
            elevation={0}
            className={GCR.hierarchyPanel}
            sx={{
                flex: '0 0 70%',
                width: '70%',
                borderRadius: '12px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                border: '1px solid rgba(25,118,210,0.12)',
                boxShadow: '0 2px 16px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04)',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2.5,
                    py: 1.5,
                    borderBottom: '1px solid rgba(25,118,210,0.08)',
                    background: 'linear-gradient(135deg, #f0f7ff 0%, #fafafa 100%)',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '8px',
                            bgcolor: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 6px rgba(25,118,210,0.3)',
                        }}
                    >
                        <AccountTreeIcon sx={{ fontSize: 15, color: '#fff' }} />
                    </Box>
                    <Typography variant="subtitle2" fontWeight={700} fontSize={13.5} letterSpacing="0.01em">
                        {t('growCategoryRestriction.hierarchy.title')}
                    </Typography>

                    {rootNodes.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', ml: '4px' }}>
                            <Button
                                size="small"
                                onClick={collapseAll}
                                startIcon={<UnfoldLessIcon sx={{ fontSize: '18px !important' }} />}
                                sx={{
                                    ...headerBtnSx,
                                    '&:hover': {
                                        borderColor: '#1976d2',
                                        color: '#1976d2',
                                        backgroundColor: 'rgba(25,118,210,0.06)',
                                    },
                                }}
                            >
                                {t('growCategoryRestriction.hierarchy.collapseAll')}
                            </Button>

                            {onClearFilter && (
                                <Button
                                    size="small"
                                    onClick={onClearFilter}
                                    startIcon={<FilterAltOffIcon sx={{ fontSize: '18px !important' }} />}
                                    sx={{
                                        ...headerBtnSx,
                                        '&:hover': {
                                            borderColor: '#ef4444',
                                            color: '#ef4444',
                                            backgroundColor: 'rgba(239,68,68,0.05)',
                                        },
                                    }}
                                >
                                    {t('growCategoryRestriction.hierarchy.clearFilter')}
                                </Button>
                            )}
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {restrictedIds.size > 0 && (
                        <Chip
                            label={t('growCategoryRestriction.hierarchy.selected', { count: restrictedIds.size })}
                            size="small"
                            sx={{
                                bgcolor: '#fee2e2',
                                color: '#b91c1c',
                                fontWeight: 700,
                                fontSize: 11,
                                height: 22,
                                borderRadius: '6px',
                                border: '1px solid #fca5a5',
                            }}
                        />
                    )}
                    {totalLoaded > 0 && (
                        <Chip
                            label={t('growCategoryRestriction.hierarchy.totalCount', { count: totalLoaded })}
                            size="small"
                            sx={{
                                bgcolor: '#f0f7ff',
                                color: '#1565c0',
                                fontWeight: 600,
                                fontSize: 11,
                                height: 22,
                                borderRadius: '6px',
                                border: '1px solid #bfdbfe',
                            }}
                        />
                    )}
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', py: '4px', position: 'relative' }}>
                {isBusy && !isLoadingRoot && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            bgcolor: 'rgba(255,255,255,0.55)',
                            backdropFilter: 'blur(1.5px)',
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'all',
                        }}
                    >
                        <CircularProgress size={36} thickness={3.5} />
                    </Box>
                )}

                {isLoadingRoot ? (
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {Array.from({ length: 7 }).map((_, i) => (
                            <Skeleton
                                key={i}
                                variant="rectangular"
                                height={34}
                                sx={{ borderRadius: '8px', mx: '4px', opacity: 1 - i * 0.1 }}
                            />
                        ))}
                    </Box>
                ) : rootNodes.length === 0 ? (
                    <Box
                        sx={{
                            p: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <AccountTreeIcon sx={{ fontSize: 36, color: 'text.disabled', opacity: 0.4 }} />
                        <Typography variant="body2" color="text.disabled" fontWeight={500}>
                            {countryId === ''
                                ? t('growCategoryRestriction.hierarchy.selectCountry')
                                : t('growCategoryRestriction.hierarchy.noData')}
                        </Typography>
                    </Box>
                ) : (
                    <TreeBranch
                        nodes={rootNodes}
                        ancestors={[]}
                        restrictedIds={restrictedIds}
                        onToggleExpand={toggleExpand}
                        onToggleRestrict={onToggleRestrict}
                    />
                )}
            </Box>
        </Paper>
    );
};
